import React from "react";

/**
 * ISO 9001 + GMP-WHO trust badges. Renders the actual certification logos
 * (PNG assets in /badges/) — the only authoritative way to signal compliance.
 *
 * Visibility is driven by the public Settings snapshot so admins can disable
 * the whole strip if an audit lapses, without touching code. Subtitle text
 * is also configurable.
 *
 * Three visual variants:
 *   - `chip`    — 2 tiny logos for shop cards (28 px tall).
 *   - `inline`  — 2 logos in a row, used in product summaries (40 px tall).
 *   - `block`   — 2 larger logos + framed card, used on product detail pages (64 px tall).
 */
const ISO_LOGO = "/badges/iso-9001.png";
const GMP_LOGO = "/badges/gmp.png";

export default function ISOCertificationBadge({ settings, variant = "inline", className = "" }) {
    if (!settings || !settings.iso_certified) return null;
    const subtitle = settings.iso_certification_subtitle || "";

    if (variant === "chip") {
        return (
            <div
                className={`flex items-center gap-1.5 ${className}`}
                data-testid="iso-badge-chip"
                title={subtitle || "ISO 9001:2015 & GMP-WHO certified"}
                aria-label={subtitle || "ISO 9001 and GMP certified"}
            >
                <img src={ISO_LOGO} alt="ISO 9001 certified" className="h-7 w-auto" loading="lazy" />
                <img src={GMP_LOGO} alt="GMP certified" className="h-7 w-auto" loading="lazy" />
            </div>
        );
    }

    if (variant === "block") {
        return (
            <div
                className={`border border-emerald-200 bg-emerald-50/60 px-4 py-3 ${className}`}
                data-testid="iso-badge-block"
            >
                <div className="flex items-center gap-4 flex-wrap">
                    <img src={ISO_LOGO} alt="ISO 9001 certified" className="h-14 sm:h-16 w-auto" loading="lazy" />
                    <img src={GMP_LOGO} alt="GMP certified" className="h-14 sm:h-16 w-auto" loading="lazy" />
                    <div className="flex-1 min-w-[180px]">
                        <div className="text-xs uppercase tracking-widest text-emerald-900 font-medium">Certified quality</div>
                        <div className="text-sm text-emerald-800 leading-snug mt-0.5">
                            {subtitle || "Manufactured under ISO 9001:2015 and GMP-WHO standards. Every batch independently audited."}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // inline (default)
    return (
        <div
            className={`flex items-center gap-2 ${className}`}
            data-testid="iso-badge-inline"
            title={subtitle || "ISO 9001:2015 & GMP-WHO certified"}
        >
            <img src={ISO_LOGO} alt="ISO 9001 certified" className="h-10 w-auto" loading="lazy" />
            <img src={GMP_LOGO} alt="GMP certified" className="h-10 w-auto" loading="lazy" />
        </div>
    );
}
